import { useSortable } from '@dnd-kit/sortable';
import { useEffect } from 'react';
import styled from 'styled-components';

import { Accordion } from '@hivebrite/design-system/Accordion';
import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';

import DraggableAccordionTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/DraggableAccordionTitle';
import { useBuilderContext, useSortableListContext } from 'hb-builder/hooks';

const DragAndDropListItem = ({
  childrenComponent: Children,
  componentName,
  currentItemNumber,
  flags,
  isInitiallyOpen,
  item,
  itemPosition,
  onChange,
  onToggleCallback,
  parentItem,
}) => {
  const { isItemOpen, updateItemOpen, allItemsClosed, removeItemOpen } =
    useSortableListContext();
  const { updateAttribute } = useBuilderContext();

  const oneItemOnly = currentItemNumber <= 1;
  const lastItem = itemPosition === currentItemNumber;
  const isOpened = isItemOpen(item.id);
  const forceOpenedId = parentItem.attributes.__isSelected;
  const isForceOpened = item.id === forceOpenedId;

  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: item.id,
  });
  const { onPointerDown } = listeners;

  const draggedBoxStyle = transform
    ? {
        transform: `translate3d(0, ${transform.y}px, 0)`,
        zIndex: 1,
      }
    : undefined;

  const onToggle = () => {
    onToggleCallback?.();
    updateItemOpen(item.id, !isOpened);

    if (isForceOpened && !isOpened) {
      updateAttribute({
        id: parentItem.id,
        attribute: '__isSelected',
        value: null,
      });

      removeItemOpen(item.id);
    }
  };

  const handleDrag = (event) => {
    if (isOpened) {
      return;
    }

    return onPointerDown(event);
  };

  const title = __('%{componentName} %{position}').i__({
    componentName,
    position: itemPosition,
  });

  useEffect(() => {
    if (isInitiallyOpen) {
      updateItemOpen(item.id, true);
    }
  }, [item]);

  useEffect(() => {
    if (forceOpenedId && isOpened) {
      removeItemOpen(item.id);
    }
  }, [forceOpenedId]);

  return (
    <Box ref={setNodeRef} {...attributes} style={draggedBoxStyle}>
      <AccordionWrapper
        isOpen={isForceOpened || isOpened}
        onToggle={onToggle}
        title={
          <DraggableAccordionTitle
            handleDrag={handleDrag}
            isDraggable={allItemsClosed}
            label={title}
          />
        }
      >
        <Children
          componentName={componentName}
          flags={flags}
          item={item}
          onChange={onChange}
          oneItemOnly={oneItemOnly}
          parentItem={parentItem}
          removeItemOpen={removeItemOpen}
        />
        {isOpened && lastItem && <Divider m="5px 0 10px 0" />}
      </AccordionWrapper>
    </Box>
  );
};

const AccordionWrapper = ({ children, isOpen, onToggle, title }) => (
  <WrapperAccordion>
    <Accordion isOpen={isOpen} onToggle={onToggle} title={title} triggerSticky>
      {({ ref }) => <WrapperSetting ref={ref}>{children}</WrapperSetting>}
    </Accordion>
  </WrapperAccordion>
);

export default DragAndDropListItem;

const WrapperAccordion = styled.div`
  width: 100%;
  > h2 {
    width: 100%;
  }
`;

const WrapperSetting = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
